import React from "react";
import date from "./dateAdapter.js";

const parseQueryString = function (query) {
  var result = Object.create(null);
  new URLSearchParams(query).forEach(function (value, key) {
    if (Object.prototype.hasOwnProperty.call(result, key)) {
      result[key] = Array.isArray(result[key])
        ? result[key].concat(value)
        : [result[key], value];
    } else {
      result[key] = value;
    }
  });
  return result;
};

const isMember = function (user) {
  if (!user) return false;
  return user.is_current_member;
};

const hasRole = function (user, reg, role) {
  if (!user) return false;
  if (user.role == "Admin") return true; // Sitewide Staff permission level -> can do anything
  if (reg.status !== "Registered" && reg.status !== "Applied") return false; // No roles on a work party unless registered
  if (role == "Volunteer") return true; // All registrants get Volunteer role
  // ACLs and CLs get ACL role
  if (
    role == "ACL" &&
    (reg.role == "Assistant Crew Leader" || reg.role == "Crew Leader")
  )
    return true;
  // CLs and Lead Ambassador get CL role
  if (
    role == "CL" &&
    (reg.role == "Crew Leader" || reg.role == "Lead Ambassador")
  )
    return true;
  return false;
};

const getGroupsForOrganizer = function (user, wp) {
  var groups = [];
  if (!user) return groups;
  if (!wp.work_party_groups) return groups;
  if (user.role == "Admin") {
    groups = wp.work_party_groups;
  } else {
    for (var i = 0; i < wp.work_party_groups.length; i++) {
      var group = wp.work_party_groups[i];
      var o = group.group_organizer;
      if (o && o._id == user.contact_id) {
        groups.push(group);
      }
    }
  }
  return groups;
};

const wpPrimarySegment = function (wp) {
  var wp_segments = wp.wp_trail_segments || [];
  var primary_trail_segment = null;
  for (var i = 0; i < wp_segments.length; i++) {
    if (wp_segments[i].primary_wp_segment) {
      primary_trail_segment = wp_segments[i].trail_segment;
      break;
    }
  }
  if (primary_trail_segment === null && wp_segments.length) {
    primary_trail_segment = wp_segments[0].trail_segment;
  }
  return primary_trail_segment;
};

function DateRange({
  start,
  end = null,
  dateFormat = "ddd, MMM D, YYYY",
  timeFormat = "h:mm a",
  showTime = true,
}) {
  if (!start) return null;
  const startDate = start ? date.tz(start, "America/Los_Angeles") : null;
  const endDate = end ? date.tz(end, "America/Los_Angeles") : null;
  if (!end) {
    return (
      <span>
        {date.format(startDate, dateFormat)}
        {showTime ? " " + date.format(startDate, timeFormat) : null}
      </span>
    );
  } else if (
    date.startOfDay(startDate).getTime() === date.startOfDay(endDate).getTime()
  ) {
    // one-day events do not show time in listings
    return <span>{date.format(startDate, dateFormat)}</span>;
  } else {
    return (
      <span>
        {date.format(startDate, dateFormat)}{" "}
        {showTime ? date.format(startDate, timeFormat) : null}–{" "}
        {date.format(endDate, dateFormat)}{" "}
        {showTime ? date.format(endDate, timeFormat) : null}
      </span>
    );
  }
}

function CheckboxesInput({ name, options, value, inline, onChange }) {
  const [selectedValues, setSelectedValues] = React.useState(() => {
    const result = new Map();
    if (value) {
      value.forEach((v) => result.set(v, true));
    }
    return result;
  });

  function handleChange(changedValue) {
    const nextValue = new Map(selectedValues);
    if (nextValue.get(changedValue)) {
      nextValue.delete(changedValue);
    } else {
      nextValue.set(changedValue, true);
    }
    setSelectedValues(nextValue);
    onChange([...nextValue.keys()], name);
  }

  return (
    <div>
      {options.map((o) => {
        var key = o.key || o.value;
        var id = name + "_" + key;
        return (
          <span key={key} style={!inline ? { display: "block" } : null}>
            <span className="custom_checkbox">
              <input
                id={id}
                type="checkbox"
                checked={selectedValues.get(o.value) === true}
                value="1"
                onChange={handleChange.bind(null, o.value)}
              />
              <label htmlFor={id}> {o.label || o.value}</label>
            </span>
          </span>
        );
      })}
    </div>
  );
}

// Helper to render form fields consistently.
// - Includes label
// - Includes validation error
function FormField(props) {
  function handleNumberKeyDown(e) {
    var key = e.keyCode || e.which;
    // backspace, tab, enter, delete
    var allowed_keys = [8, 9, 13, 46];
    if (props.step) {
      // comma, period
      allowed_keys.push(188);
      allowed_keys.push(190);
    }
    if ((key < 48 || key > 57) && allowed_keys.indexOf(key) < 0) {
      if (e.preventDefault) e.preventDefault();
    }
  }

  var {
    errors,
    type,
    name,
    label,
    icon,
    help,
    label_class,
    inline,
    flexBasis,
    ...props
  } = props;
  var error = errors ? errors[name] : null;
  error = error ? <div className="validation-error">{error}</div> : "";
  var field;
  var fieldClass = inline ? "field-inline" : "field";
  var style = flexBasis ? { flexBasis: flexBasis } : {};
  var fieldProps = {
    className: fieldClass,
    style,
  };
  if (["text", "email", "tel", "date", "time"].includes(type)) {
    return (
      <div key={name} {...fieldProps}>
        {label ? <label htmlFor={name}>{label}</label> : ""}
        <input {...props} name={name} type={type} />
        {help ? <em>{help}</em> : ""}
        {error}
      </div>
    );
  } else if (type == "number") {
    return (
      <div key={name} {...fieldProps}>
        {label ? <label htmlFor={name}>{label}</label> : ""}
        <input
          {...props}
          name={name}
          type={type}
          onKeyDown={handleNumberKeyDown}
        />
        {help ? <em>{help}</em> : ""}
        {error}
      </div>
    );
  } else if (type == "select") {
    field = (
      <div>
        {label ? <label htmlFor={name}>{label}</label> : ""}
        <div className="custom_select">
          <select {...props} name={name}>
            {props.children}
          </select>
        </div>
      </div>
    );
  } else if (type == "checkbox") {
    var id = name + "_" + props.value;
    var labelclass = props.hasOwnProperty("label_class")
      ? props.label_class
      : "";
    field = (
      <div className="custom_checkbox">
        <input {...props} id={id} name={name} type="checkbox" />
        <label htmlFor={id} className={labelclass}>
          {icon ? (
            <span>
              <img src={icon} style={{ width: 50 }} />
            </span>
          ) : (
            ""
          )}{" "}
          {label || ""}
        </label>
      </div>
    );
  } else if (type == "radio") {
    var id = name + "_" + props.value;
    field = (
      <span>
        <input {...props} id={id} name={name} type="radio" />
        <label htmlFor={id}> {label || props.value}</label>
      </span>
    );
  } else if (type == "checkboxes") {
    field = <CheckboxesInput {...props} name={name} inline={inline} />;
  } else if (type == "textarea") {
    field = (
      <div>
        {label ? <label htmlFor={name}>{label}</label> : ""}
        <textarea {...props} name={name} type="textarea" />
      </div>
    );
  } else {
    throw "Unsupported field type: " + props.type;
  }
  return (
    <div key={name} {...fieldProps}>
      {field}
      {help ? <em>{help}</em> : ""}
      {error}
    </div>
  );
}

const STATES = [
  "- State -",
  "AL",
  "AK",
  "AZ",
  "AR",
  "CA",
  "CO",
  "CT",
  "DC",
  "DE",
  "FL",
  "GA",
  "HI",
  "ID",
  "IL",
  "IN",
  "IA",
  "KS",
  "KY",
  "LA",
  "ME",
  "MD",
  "MA",
  "MI",
  "MN",
  "MS",
  "MO",
  "MT",
  "NE",
  "NV",
  "NH",
  "NJ",
  "NM",
  "NY",
  "NC",
  "ND",
  "OH",
  "OK",
  "OR",
  "PA",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VT",
  "VA",
  "WA",
  "WV",
  "WI",
  "WY",
];

const RACES = [
  "Prefer not to say",
  "Alaskan Native",
  "American Indian",
  "Asian",
  "Black",
  "Latino",
  "Pacific Islander",
  "White",
  "Multi-Racial/Multi-Cultural",
  "Race/ethnicity is not represented here",
];

const GENDERS = [
  "--",
  "Agender",
  "Genderqueer",
  "Non-conforming",
  "Man",
  "Trans Man",
  "Trans Woman",
  "Woman",
  "Gender is not represented here",
];

const SHIRT_SIZES = [
  "--",
  "Fitted X-Small",
  "Fitted Small",
  "Fitted Medium",
  "Fitted Large",
  "Fitted X-Large",
  "Fitted XX-Large",
  "Fitted XXX-Large",
  "Unisex X-Small",
  "Unisex Small",
  "Unisex Medium",
  "Unisex Large",
  "Unisex X-Large",
  "Unisex XX-Large",
  "Unisex XXX-Large",
];

function Modal(props) {
  function getDefaultProps() {
    return { open: false };
  }

  props = {
    ...getDefaultProps(),
    ...props,
  };

  function getInitialState() {
    return { open: props.open };
  }

  const [state, setState] = React.useState(getInitialState);

  function toggleOpen(e) {
    if (e !== undefined) e.preventDefault();
    // prevent body from scrolling when modal is open
    document.body.classList.toggle("modal-open", !state.open);
    if (state.open) {
      document.querySelectorAll(".modal-backdrop").forEach(function (backdrop) {
        backdrop.remove();
      });
    } else {
      var backdrop = document.createElement("div");
      backdrop.className = "modal-backdrop";
      document.body.appendChild(backdrop);
    }
    setState((currentState) => ({
      ...currentState,
      open: !currentState.open,
    }));
    // it appears that the state for open is not immediately transformed by
    // the toggle above, so we really want to do this when the state *is*
    // 'open'
    if (state.open && props.onClosed) {
      props.onClosed();
    }
  }

  var { Component, component, ...props } = props;
  if (Component !== undefined) {
    component = <Component {...props} closeModal={toggleOpen} />;
  }
  var trigger = props.children || (
    <a href="#" className="ui help js-toggle"></a>
  );
  return (
    <span className="modal-trigger">
      {trigger ? <span onClick={toggleOpen}>{trigger}</span> : ""}
      <div className={"modal-wrapper" + (state.open ? " on" : "")} id="modal">
        <div className="modal-inner">
          <a
            onClick={toggleOpen}
            onTouchStart={toggleOpen}
            className="ui close js-toggle"
          ></a>
          {state.open ? component : ""}
        </div>
      </div>
    </span>
  );
}

const age = function (birthdate) {
  // birthdate may be a Date or ISO string
  return date.differenceInYears(date.now(), date.toDate(birthdate));
};

const forceHttps = function (url) {
  if (url && window.base_url.search(/https:/) == 0) {
    url = url.replace("http://www.wta.org", "https://www.wta.org");
    url = url.replace("http://wta.org", "https://wta.org");
    url = url.replace("http://staging.wta.org", "https://staging.wta.org");
  }
  return url;
};

export {
  DateRange,
  FormField,
  GENDERS,
  Modal,
  RACES,
  SHIRT_SIZES,
  STATES,
  age,
  forceHttps,
  getGroupsForOrganizer,
  hasRole,
  isMember,
  parseQueryString,
  wpPrimarySegment,
};
